import { Alert, InputNumber, Slider, Typography } from 'antd';
import clsx from 'clsx';
import { InfoButton } from 'components';
import Colors from 'const/colors';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatEUR, numberToLocaleString } from 'utils/number';
import { REPARTITION_MAX, SliderValue } from './step3PersonneMorale.model';

const sliderHandleBaseClassName =
  '[&_.ant-slider-handle::after]:!-top-1 [&_.ant-slider-handle::after]:!h-4 [&_.ant-slider-handle::after]:!w-4 [&_.ant-slider-handle::after]:!border-[3px] [&_.ant-slider-handle::after]:!shadow-none';
const concessionnaireSliderHandleClassName = clsx(
  sliderHandleBaseClassName,
  '[&_.ant-slider-handle::after]:!border-orange-particuliers-6',
);
const beneficiaireSliderHandleClassName = clsx(
  sliderHandleBaseClassName,
  '[&_.ant-slider-handle::after]:!border-bleu-hellio-6',
);

const currencyInputParser = (value: string | undefined) =>
  Number(String(value || '').replace(/[^\d.]/g, ''));

interface Step3MoraleRepartitionSectionProps {
  primeParVehiculeValue: number;
  primeConcessionnaire: number;
  primeBeneficiaire: number;
  beneficiairePercent: number;
  summaryBefore: string;
  summaryAfter: string;
  primeErrorMessage: string | null;
  onConcessionnaireInputChange: (value: number | null) => void;
  onBeneficiaireInputChange: (value: number | null) => void;
  onConcessionnaireSliderChange: (value: SliderValue) => void;
  onBeneficiaireSliderChange: (value: SliderValue) => void;
}

export default function Step3MoraleRepartitionSection({
  primeParVehiculeValue,
  primeConcessionnaire,
  primeBeneficiaire,
  beneficiairePercent,
  summaryBefore,
  summaryAfter,
  primeErrorMessage,
  onConcessionnaireInputChange,
  onBeneficiaireInputChange,
  onConcessionnaireSliderChange,
  onBeneficiaireSliderChange,
}: Step3MoraleRepartitionSectionProps) {
  const { t } = useTranslation();

  return (
    <section>
      <Typography.Title
        level={4}
        className="mb-4 !text-base !text-gris-hellio-6"
      >
        {t(
          'nouveau_formulaire:resultat:repartition_concessionnaire_beneficiaire',
        )}
      </Typography.Title>

      <div className="space-y-4">
        <div>
          <div className="mb-2 flex items-center justify-between gap-4">
            <span>
              {t('nouveau_formulaire:resultat:prime_concessionnaire')}
              <InfoButton
                isBlue
                tooltip={t('nouveau_formulaire:resultat:montant_min_prime', {
                  max: REPARTITION_MAX,
                })}
              />
            </span>
            <InputNumber
              value={Number(primeConcessionnaire.toFixed(2))}
              min={0}
              max={primeParVehiculeValue}
              precision={2}
              step={1}
              disabled={!primeParVehiculeValue}
              className="text-sm md:!w-[79px]"
              onChange={onConcessionnaireInputChange}
              formatter={(value) => `${value ?? 0} €`}
              parser={currencyInputParser}
            />
          </div>

          <Slider
            min={0}
            max={primeParVehiculeValue}
            step={0.01}
            value={Number(primeConcessionnaire.toFixed(2))}
            disabled={!primeParVehiculeValue}
            onChange={onConcessionnaireSliderChange}
            tooltip={{
              formatter: (value) => formatEUR(Number(value || 0), 2),
            }}
            styles={{
              track: { background: Colors.OrangeHellio },
            }}
            className={concessionnaireSliderHandleClassName}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-4">
            <span>{t('nouveau_formulaire:resultat:prime_beneficiaire')}</span>
            <InputNumber
              value={Number(primeBeneficiaire.toFixed(2))}
              min={0}
              max={primeParVehiculeValue}
              precision={2}
              step={1}
              disabled={!primeParVehiculeValue}
              className="text-sm md:!w-[79px]"
              onChange={onBeneficiaireInputChange}
              formatter={(value) => `${value ?? 0} €`}
              parser={currencyInputParser}
            />
          </div>

          <Slider
            min={0}
            max={primeParVehiculeValue}
            step={0.01}
            value={Number(primeBeneficiaire.toFixed(2))}
            disabled={!primeParVehiculeValue}
            onChange={onBeneficiaireSliderChange}
            reverse
            tooltip={{
              formatter: (value) => formatEUR(Number(value || 0), 2),
            }}
            styles={{
              track: { background: Colors.BleuHellio },
            }}
            className={beneficiaireSliderHandleClassName}
          />
        </div>
      </div>

      <p className="mt-4 text-sm leading-6 text-gris-hellio-6">
        {summaryBefore}
        <span className="font-semibold text-bleu-hellio-6">
          {numberToLocaleString(beneficiairePercent, '%', 0)}
        </span>
        {summaryAfter}
      </p>

      {primeErrorMessage && (
        <Alert
          type="error"
          showIcon
          className="mt-4"
          message={primeErrorMessage}
        />
      )}
    </section>
  );
}
